import { FC } from "react";
import { hexToString, toBytes } from "viem";

import { useAllowance, useMetamask, useServerSignature, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import {
  AddressType,
  type IAssetPromo,
  type IContract,
  type ILootBox,
  type IMysteryBox,
  ModuleType,
} from "@framework/types";
import PurchaseABI from "@framework/abis/json/ExchangePurchaseFacet/purchase.json";
import PurchaseMysteryABI from "@framework/abis/json/ExchangeMysteryBoxFacet/purchaseMystery.json";
import { ZeroAddress } from "@framework/constants";

interface IPromoWithMystery extends IAssetPromo {
  box?: IMysteryBox | ILootBox;
}

interface IPromoPurchaseButtonProps {
  className?: string;
  disabled?: boolean;
  promo: IPromoWithMystery;
  variant?: ListActionVariant;
}

export const PromoPurchaseButton: FC<IPromoPurchaseButtonProps> = props => {
  const { className, disabled, promo, variant = ListActionVariant.button } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  // TODO use native buttons
  // switch (promo.item?.components[0].contract!.contractModule){
  //   case ModuleType.MYSTERY:
  //     return <MysteryBoxPurchaseButton mysteryBox={promo.box as IMysteryBox} />
  //   case ModuleType.LOOT:
  //     return <LootBoxPurchaseButton lootBox={promo.box as ILootBox} />
  //   case ModuleType.HIERARCHY:
  //     return <TemplatePurchaseButton template={promo as ITemplate} />
  //   default: // RAFFLE
  //     throw new Error("unsupported token type");
  // }

  // TODO LootBox
  const mysteryComponents = promo.item?.components.filter(
    component => component.contract!.contractModule === ModuleType.MYSTERY,
  );

  const referrer = ZeroAddress;

  const { execute: metaFnWithAllowance } = useAllowance(async (sign: IServerSignature, systemContract: IContract) => {
    const items = convertDatabaseAssetToChainAsset(promo.box?.content?.components);
    const promoItem = convertDatabaseAssetToChainAsset(promo.item?.components);
    const price = convertDatabaseAssetToChainAsset(promo.price?.components);

    return mysteryComponents && mysteryComponents.length > 0
      ? writeContractAsync({
          address: systemContract.address as AddressType,
          abi: PurchaseABI.concat(PurchaseMysteryABI),
          functionName: "purchaseMystery",
          args: [
            {
              externalId: promo.id,
              expiresAt: sign.expiresAt,
              nonce: toBytes(sign.nonce),
              extra: hexToString("0x", { size: 32 }),
              receiver: promo.merchant!.wallet,
              referrer,
            },
            [...items, promoItem[0]],
            price,
            sign.signature,
            {
              value: getEthPrice(promo.price),
            },
          ],
        })
      : writeContractAsync({
          address: systemContract.address as AddressType,
          abi: PurchaseABI.concat(PurchaseMysteryABI),
          functionName: "purchase",
          args: [
            {
              externalId: promo.id,
              expiresAt: sign.expiresAt,
              nonce: toBytes(sign.nonce, { size: 32 }),
              extra: hexToString("0x", { size: 32 }),
              receiver: promo.merchant!.wallet,
              referrer,
            },
            promoItem[0],
            price,
            sign.signature,
            {
              value: getEthPrice(promo.price),
            },
          ],
        });
  });

  const metaFnWithSign = useServerSignature((_values: null, sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToTokenTypeAsset(promo.price?.components);
    return metaFnWithAllowance(
      {
        contract: systemContract.address,
        assets: price,
      },
      sign,
      systemContract,
    );
  });

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/promos/sign",
        method: "POST",
        data: {
          referrer,
          promoId: promo.id,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handleBuy = async () => {
    await metaFn();
  };

  return (
    <ListAction
      onClick={handleBuy}
      message="form.buttons.buy"
      className={className}
      dataTestId="PromoPurchaseButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
